import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { INDI_CUSTOMERS_URL, SALESORDERS_SERVICE_URL } from 'src/app/injection-tokens';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { forkJoin, Observable } from 'rxjs';
import { IndiCustomer } from '../indi-customer';
import { ApiResponse } from '../api-response';
import { APIVerkooporders } from '../APIVerkooporders';
import { APIVerkoopordersToWrite } from '../APIVerkoopordersToWrite';
import { User } from '../user';
import { ICMCustomerNotes } from '../ICMCustomerNotes';
import { ManuallyCreatedRequest } from '../manually-created-request';
import {
  VerkoopordersCustomerPreferences,
  VerkoopordersCustomerPreferencesToWrite,
} from '../VerkoopordersCustomerPreferences';

export interface CombinedData {
  APIVerkooporders: APIVerkooporders;
  indiCustomer: IndiCustomer;
  users: User[];
  ICMCustomerNotes: ICMCustomerNotes;
  verkoopordersCustomerPreferences: VerkoopordersCustomerPreferences;
}

@Injectable({
  providedIn: 'root',
})
export class NewSalesorderService {
  constructor(
    @Inject(SALESORDERS_SERVICE_URL) readonly salesordersServiceURL: string,
    @Inject(INDI_CUSTOMERS_URL) readonly indiCustomersUrl: string,
    private authService: AuthService,
    readonly http: HttpClient
  ) {}

  getCombinedData(ids: number | number[]): Observable<CombinedData> {
    const idArray = Array.isArray(ids) ? ids : [ids];
    return this.getAPIVerkooporders(idArray).pipe(
      switchMap((ApiVerkooporders: APIVerkooporders) => {
        const customerNumber = ApiVerkooporders.verkooporder.customerNumber;
        return forkJoin({
          indiCustomers: this.getIndiCustomers(customerNumber),
          users: this.getUsers(customerNumber),
          icmCustomerNotes: this.getIcmNote(customerNumber),
          verkoopordersCustomerPreferences: this.getVerkoopordersCustomerPreferences(customerNumber),
        }).pipe(
          map(({ indiCustomers, users, icmCustomerNotes, verkoopordersCustomerPreferences }) => ({
            APIVerkooporders: ApiVerkooporders,
            indiCustomer: indiCustomers[0],
            users,
            ICMCustomerNotes: icmCustomerNotes,
            verkoopordersCustomerPreferences,
          }))
        );
      }),
      shareReplay(1)
    );
  }

  getAPIVerkooporders(ids: number[]): Observable<APIVerkooporders> {
    let url = this.salesordersServiceURL + '?id=';
    url += ids.join('&id=');
    url += '&updatedByHint=' + this.authService.getUserInfo().fullName;
    return this.http.get<APIVerkooporders>(url);
  }

  getIndiCustomers(customerNumber: string): Observable<IndiCustomer[]> {
    return this.http.get<IndiCustomer[]>(
      this.indiCustomersUrl + '?customerNumber=' + customerNumber + '&fetchSegments=true'
    );
  }

  getUsers(customerNumber: string): Observable<User[]> {
    return this.http.get<User[]>(this.indiCustomersUrl + '/' + customerNumber + '/user-email-preferences');
  }

  getIcmNote(customerNumber: string): Observable<ICMCustomerNotes> {
    return this.http.get<ICMCustomerNotes>(this.indiCustomersUrl + '/' + customerNumber + '/notes');
  }

  postAPIVerkoopordersToWrite(salesorders: APIVerkoopordersToWrite): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(this.salesordersServiceURL, salesorders);
  }

  markVerkoopordersAsManuallyCreated(request: ManuallyCreatedRequest): Observable<void> {
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    return this.http.patch<void>(`${this.salesordersServiceURL}/manuallycreated`, request);
  }

  getVerkoopordersCustomerPreferences(customerNumber: string): Observable<VerkoopordersCustomerPreferences> {
    return this.http.get<VerkoopordersCustomerPreferences>(
      `${this.salesordersServiceURL}/customer-preferences/${customerNumber}`
    );
  }

  setVerkoopordersCustomerPreferences(
    customerNumber: string,
    verkoopordersCustomerPreferencesToWrite: VerkoopordersCustomerPreferencesToWrite
  ): Observable<VerkoopordersCustomerPreferences> {
    return this.http.put<VerkoopordersCustomerPreferences>(
      `${this.salesordersServiceURL}/customer-preferences/${customerNumber}`,
      verkoopordersCustomerPreferencesToWrite
    );
  }
}
